// IE 2026 11 01 / ODOO-350449 (заказ Игоря: режими відображення комірок p149, T2)
// Отложенные данные видов ячеек отчёта REPORT2 (§1.3, §1.5 плана p149). Ответ метода виджета select_cell_views:
//   { command: 'updateDoc', set_value: [{ binding: 'record._doc.cell_views', value: { <вид>: { <path>: { <date_key>: { <спутник>: <значение> } } } } },
//     { binding: 'record._doc.cell_views_stats', value: stats }], cell_views_stats: stats, partial: bool }; stats (T0) = { views, skipped, pending, budget_s, state, time }
// path — индексный путь строки в дереве _doc.data ('0.3.1' = data[0].attrs[3].attrs[1]); строки дерева в отложенном ответе не пересобираются (тот же проход
// бэка) и актуальны, только если основной ответ с тех пор не менялся (версия — mainStore.cellView_state). Слияние атомарно: любая строка по пути
// отсутствует (или, если бэк прислал stats.rows, число строк не совпало) — весь вид отбрасывается ({ dropped: true }), ячейки не трогаются.
// Чистые функции (без Vue и DOM) — tests/unit/cellViewsMerge.test.mjs. Выключатель fe.ux.cellViewsDeferred (по умолчанию ВКЛ): ВЫКЛ — фронт не
// запрашивает и не сливает отложенные данные видов (вид без данных в основном ответе показывает числа)
export const CELL_VIEWS_DEFERRED_FLAG = 'fe.ux.cellViewsDeferred'
export const CELL_VIEWS_BINDING = 'record._doc.cell_views'
export const CELL_VIEWS_STATS_BINDING = 'record._doc.cell_views_stats'
export const CELL_VIEWS_METHOD = 'select_cell_views'
const CHILD_KEY = 'attrs'
const SCAN_MAX = 5000   // потолок обхода строк при поиске спутников (признак «основной ответ считал вид»)

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v)
const badKey = (k) => !k || k.charAt(0) === '_' || k === '__proto__' || k === 'constructor' || k === 'prototype'

export function cellViews_deferredOn(setting) {   // чтение флага (нет читателя / ошибка — ВКЛ)
    try { return typeof setting === 'function' ? setting(CELL_VIEWS_DEFERRED_FLAG, true) !== false : true } catch (e) { return true }
}

// число строк дерева (рекурсивно по childKey) — сверка со stats.rows ответа
export function cellViews_rowCount(docData, childKey = CHILD_KEY) {
    if (!Array.isArray(docData)) return 0
    let n = 0
    const stack = [docData]
    while (stack.length) {
        const arr = stack.pop()
        for (const it of arr) {
            n++
            const kids = it && it[childKey]
            if (Array.isArray(kids) && kids.length) stack.push(kids)
        }
    }
    return n
}

// строка дерева по индексному пути '0.3.1' | null
export function cellViews_rowAt(docData, path, childKey = CHILD_KEY) {
    if (!Array.isArray(docData)) return null
    const parts = String(path ?? '').split('.')
    let arr = docData, row = null
    for (let i = 0; i < parts.length; i++) {
        const p = parts[i]
        if (!/^\d+$/.test(p) || !Array.isArray(arr)) return null
        row = arr[Number(p)]
        if (!row || typeof row !== 'object') return null
        arr = row[childKey]
    }
    return row
}

// слить данные ОДНОГО вида в дерево: viewPayload = { path: { date_key: { спутник: значение } } }.
// opts: { rows — число строк дерева у бэка (stats.rows; T0 его не шлёт — тогда не сверяется, отброс только по пути), childKey ('attrs') }.
// → число применённых ячеек-дат | { dropped: true, reason, path? } — ничего не изменено
export function mergeCellViews(docData, viewPayload, opts = {}) {
    const childKey = opts.childKey || CHILD_KEY
    if (!Array.isArray(docData)) return { dropped: true, reason: 'no data' }
    if (!isObj(viewPayload)) return { dropped: true, reason: 'no payload' }
    const rows = opts.rows
    if (rows !== undefined && rows !== null && rows !== '' && Number(rows) !== cellViews_rowCount(docData, childKey)) {
        return { dropped: true, reason: 'row count mismatch' }
    }
    const plan = []
    for (const path of Object.keys(viewPayload)) {
        const dates = viewPayload[path]
        if (!isObj(dates)) continue
        const row = cellViews_rowAt(docData, path, childKey)
        if (!row) return { dropped: true, reason: 'row not found', path }
        plan.push([row, dates])
    }
    let n = 0
    for (const [row, dates] of plan) {
        for (const dk of Object.keys(dates)) {
            const sats = dates[dk]
            if (badKey(dk) || !isObj(sats)) continue
            let cell = row[dk]
            if (cell !== undefined && cell !== null && !isObj(cell)) continue   // не объект ячейки-даты — не трогаем
            let put = false
            for (const sk of Object.keys(sats)) {
                if (badKey(sk)) continue
                if (!cell) cell = row[dk] = {}
                cell[sk] = sats[sk]
                put = true
            }
            if (put) n++
        }
    }
    return n
}

// разбор ответа select_cell_views (команда updateDoc) → { views: { вид: payload }, stats, partial } | null (не ответ видов ячеек)
export function cellViews_answerOf(action) {
    if (!action || typeof action !== 'object' || !Array.isArray(action.set_value)) return null
    const sv = action.set_value.find(r => r && r.binding === CELL_VIEWS_BINDING)
    if (!sv) return null
    return {
        views: isObj(sv.value) ? sv.value : {},
        stats: isObj(action.cell_views_stats) ? action.cell_views_stats : {},
        partial: !!action.partial,
        rest: action.set_value.filter(r => r !== sv && !(r && r.binding === CELL_VIEWS_STATS_BINDING)),
    }
}

// есть ли в дереве хоть одна ячейка-дата со спутником из списка (обход ограничен SCAN_MAX строками)
export function cellViews_hasSatellites(docData, satellites, childKey = CHILD_KEY) {
    if (!Array.isArray(docData) || !Array.isArray(satellites) || !satellites.length) return false
    let seen = 0
    const stack = [docData]
    while (stack.length) {
        const arr = stack.pop()
        for (const it of arr) {
            if (++seen > SCAN_MAX) return false
            if (!it || typeof it !== 'object') continue
            for (const k of Object.keys(it)) {
                if (!/^\d{4}-\d{2}-\d{2}/.test(k)) continue
                const c = it[k]
                if (isObj(c) && satellites.some(s => c[s] !== undefined && c[s] !== null)) return true
            }
            const kids = it[childKey]
            if (Array.isArray(kids) && kids.length) stack.push(kids)
        }
    }
    return false
}

// данные вида готовы: вид не требует данных бэка → true; основной ответ считал этот вид (doc.cell_views_stats.active === view или спутники есть в
// ячейках) → true; отложенные данные вида уже пришли (doc.cell_views[view]) → true; иначе false.
// entry — запись регистра вида ({ key, needsData, satellites }); aliases — имена вида у бэка (status_pie для status_zones)
export function cellView_dataReadyOf(doc, entry, opts = {}) {
    if (!entry || !entry.needsData) return true
    const names = [entry.key, ...(Array.isArray(opts.aliases) ? opts.aliases : [])]
    const d = isObj(doc) ? doc : {}
    const st = isObj(d.cell_views_stats) ? d.cell_views_stats : {}
    if (st.active !== undefined && names.includes(st.active)) return true
    const sv = st.views   // T0: stats.views — виды, посчитанные основным ответом (список или словарь)
    if (Array.isArray(sv) ? names.some(n => sv.includes(n)) : (isObj(sv) && names.some(n => sv[n] !== undefined && sv[n] !== false))) return true
    if (isObj(d.cell_views) && names.some(n => d.cell_views[n] !== undefined)) return true
    return cellViews_hasSatellites(d.data, entry.satellites, opts.childKey || CHILD_KEY)
}

// бэк знает отложенный расчёт видов: основной ответ несёт doc.cell_views_stats (T0). Старый бэк — отложенный запрос не шлём (метода нет)
export function cellViews_backendSupports(doc) {
    return isObj(doc) && isObj(doc.cell_views_stats)
}
